/** Таблица «Подбора поставки»: дерево товар → регионы (tanstack, раскрытие и сортировка по
 *  уровням) с виртуализированным телом. Как у «Распределения»: subRows материализуются ТОЛЬКО у
 *  раскрытых товаров (сотни офферов × регионы), раскрываемость — по числу видимых регионов;
 *  выключенные регионы скрыты, пока не включён «показывать выключенные» (тогда — серым). Ввод
 *  количества — только у региона; у товара — сумма по включённым регионам. «Нужно» — ввод /
 *  «до N дней»; «Квант» — штук в коробе (1С, по nm); «С квантом» — «Нужно» вверх до кванта по
 *  региону (draftQuant.ts), серым, пока «Округлять до кванта» выключено. */
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
  type OnChangeFn,
  type Row,
  type SortingState,
} from "@tanstack/react-table";
import { AlertTriangle, ArrowDown, ArrowUp, ChevronDown, ChevronRight } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { mpProductUrl } from "@/lib/marketplace";
import { cn } from "@/lib/utils";
import { int } from "../ozon-analytics/format";
import { Days, PerDay, Qty } from "./cells";
import { quantPartialNote, quantProblem, type Quant } from "./draftQuant";
import type { Draft } from "./draftStorage";
import { aggregate, isMarked, leafFinal, leafQty, type DraftLeaf, type DraftProduct, type ProductAgg } from "./draftTree";
import { DEFICIT_STYLE, NO_DEMAND } from "./format";
import { parseQty } from "./supplyDraft";

const ROW_H = 30;
const GRID = "240px 130px 90px 60px 100px minmax(200px, 1fr) 100px 90px 90px 80px 80px 100px 100px";
const MIN_W = "min-w-[1480px]";

/** Строка дерева: товар (`agg`) или регион товара (`leaf`). */
export interface TRow {
  id: string;
  p: DraftProduct;
  leaf?: DraftLeaf;
  agg?: ProductAgg;
  /** видимых регионов (у нераскрытого товара subRows пусты, а знать надо) */
  nKids: number;
  /** количество для сортировки — из снимка, а не из живого ввода (см. DraftTable) */
  sortQty: number;
  /** выключенных регионов, скрытых из таблицы */
  hiddenOff: number;
  subRows: TRow[];
}

/** id строки товара. Без offer_key уникального поля у оффера нет (nm, артикул, размер — nullable), поэтому
 *  id — из этих полей плюс номер среди совпадающих в `shown`: не зависит от фильтров, пока дублей нет. */
function productRowId(p: DraftProduct, seen: Map<string, number>): string {
  if (p.key !== null) return p.key;
  const o = p.offer;
  const base = JSON.stringify(["nokey", o.legal_name, o.mp_sku, o.vendor_code, o.mp_size]);
  const n = seen.get(base) ?? 0;
  seen.set(base, n + 1);
  return n === 0 ? base : `${base}#${n}`;
}

function isRowExpanded(expanded: ExpandedState, id: string): boolean {
  return expanded === true || expanded[id] === true;
}

function buildTree(
  shown: DraftProduct[],
  qty: Draft["qty"],
  sortQty: Draft["qty"],
  showOff: boolean,
  expanded: ExpandedState,
  round: boolean,
): TRow[] {
  const seen = new Map<string, number>();
  return shown.map((p) => {
    const id = productRowId(p, seen);
    const vis = p.leaves.filter((l) => l.on || showOff);
    return {
      id,
      p,
      agg: aggregate(p, qty, round),
      nKids: vis.length,
      sortQty: aggregate(p, sortQty, round).qty,
      hiddenOff: p.leaves.length - vis.length,
      // subRows ТОЛЬКО у раскрытых: react-table строит и сортирует все уровни независимо от раскрытия
      subRows: isRowExpanded(expanded, id)
        ? vis.map((l) => ({
            id: `${id}|${l.regionId}`,
            p,
            leaf: l,
            nKids: 0,
            sortQty: leafQty(l, sortQty),
            hiddenOff: 0,
            subRows: [],
          }))
        : [],
    };
  });
}

const muted = (text: string, title?: string): React.ReactElement => (
  <span className="text-muted-foreground" title={title}>
    {text}
  </span>
);

/** Число строки: у региона — ячейка витрины, у товара — сумма по включённым регионам. */
const num = (r: TRow, k: "orders_fbo_28d" | "fbo_stock" | "cover_days_total" | "orders_28d"): number | null =>
  r.leaf ? r.leaf.cell[k] : r.agg![k];

/** Значок «не округлено» с причиной в подсказке. */
const warn = (title: string): React.ReactElement => (
  <span title={title} className="ml-1 inline-flex text-amber-600 dark:text-amber-400" data-testid="draft-quant-warn">
    <AlertTriangle className="size-3" aria-label={title} />
  </span>
);

/** Квант товара: число / «не задан» / «конфликт» / «…» (не загружен); у региона — тем же, серым. */
function QuantCell({ q, leaf }: { q: Quant; leaf: boolean }): React.ReactElement {
  const problem = quantProblem(q);
  if (q.kind === "ok") {
    const partial = quantPartialNote(q);
    return (
      <span className={cn("inline-flex items-center tabular-nums", leaf && "text-muted-foreground")}>
        {int(q.inBox)}
        {partial !== null && warn(partial)}
      </span>
    );
  }
  const text = q.kind === "conflict" ? "конфликт" : q.kind === "unloaded" ? "…" : "не задан";
  return (
    <span
      className={cn(leaf ? "text-muted-foreground" : q.kind === "conflict" ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground")}
      title={problem ?? undefined}
      data-testid="draft-quant"
      data-quant={q.kind}
    >
      {text}
    </span>
  );
}

/** Сортировка: null — в конец (accessor отдаёт undefined). */
const sortable = (v: number | string | null): number | string | undefined => (v === null ? undefined : v);

function QtyInput({
  leafKey,
  qty,
  label,
  disabled,
  onQty,
  onCommit,
}: {
  leafKey: string;
  qty: number | undefined;
  label: string;
  disabled: boolean;
  onQty: (key: string, qty: number | null) => void;
  onCommit: () => void;
}): React.ReactElement {
  return (
    <input
      type="text"
      inputMode="numeric"
      aria-label={`Количество: ${label}`}
      value={qty === undefined ? "" : String(qty)}
      disabled={disabled}
      title={disabled ? "Регион выключен («Куда планируем») — количество не считается и не выгружается" : undefined}
      onClick={(e) => e.stopPropagation()}
      // ввод закончен — только теперь сортировка по «Количеству» видит новое значение (иначе строка уезжает из-под курсора)
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit();
      }}
      data-qty-input=""
      onChange={(e) => {
        // прочий ввод не принимается (поле не меняется)
        const v = parseQty(e.target.value);
        if (v !== undefined) onQty(leafKey, v);
      }}
      className="h-6 w-20 rounded border bg-background px-1.5 text-right text-xs tabular-nums disabled:cursor-not-allowed"
      data-testid="draft-qty"
    />
  );
}

/** Живые значения для ячеек. Колонки — стабильные по ссылке (`flexRender` делает из функции ячейки
 *  тип компонента: новая функция на каждый ввод пересоздавала бы поле количества и сбивала фокус),
 *  поэтому черновик и обработчики читаются из ref, как `ctxRef` у «Распределения». */
interface Ctx {
  draft: Draft;
  /** «Округлять до кванта» */
  round: boolean;
  onQty: (key: string, qty: number | null) => void;
  onToggleSel: (key: string) => void;
  onCommit: () => void;
}

function columns(ctx: React.MutableRefObject<Ctx>): ColumnDef<TRow>[] {
  const label = (r: TRow): string => `${r.p.offer.vendor_code ?? r.p.offer.mp_sku ?? "оффер"}${r.leaf ? ` · ${r.leaf.region}` : ""}`;
  return [
    {
      id: "article",
      header: "Артикул / регион",
      accessorFn: (r) => (r.leaf ? r.leaf.region : (r.p.offer.vendor_code ?? "")),
      cell: ({ row }) => {
        const r = row.original;
        if (r.leaf) return <span className="truncate pl-9">{r.leaf.region}</span>;
        const open = row.getIsExpanded();
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <button
              type="button"
              aria-label={`${open ? "Свернуть" : "Раскрыть"}: ${label(r)}`}
              aria-expanded={open}
              disabled={!row.getCanExpand()}
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              className="shrink-0 disabled:opacity-30"
            >
              {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            </button>
            {r.p.key === null ? (
              <span className="w-[13px] text-center text-muted-foreground" title="offer_key не определён (размер) — не отмечается, количества нет">
                —
              </span>
            ) : (
              <input
                type="checkbox"
                aria-label={`Отметить товар: ${label(r)}`}
                checked={isMarked(ctx.current.draft.sel, r.p)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => ctx.current.onToggleSel(r.p.key!)}
              />
            )}
            <span className="truncate">{r.p.offer.vendor_code ?? muted("без артикула")}</span>
            {r.hiddenOff > 0 && (
              <span className="shrink-0 text-[10px] font-normal text-muted-foreground" title="Выключенные регионы («Куда планируем») скрыты">
                {r.hiddenOff} выкл.
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: "legal",
      header: "Кабинет",
      enableSorting: false,
      cell: ({ row }) => (row.original.leaf ? null : <span className="truncate">{row.original.p.offer.legal_name}</span>),
    },
    {
      id: "nm",
      header: "nm",
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.leaf) return null;
        const o = row.original.p.offer;
        const url = mpProductUrl("wb", o.mp_sku);
        return url ? (
          <a href={url} target="_blank" rel="noreferrer" className="hover:underline" onClick={(e) => e.stopPropagation()}>
            {o.mp_sku}
          </a>
        ) : (
          muted("нет")
        );
      },
    },
    {
      id: "size",
      header: "Размер",
      enableSorting: false,
      cell: ({ row }) => (row.original.leaf ? null : (row.original.p.offer.mp_size ?? "—")),
    },
    {
      id: "art1c",
      header: "Артикул 1С",
      enableSorting: false,
      cell: ({ row }) => (row.original.leaf ? null : (row.original.p.offer.article_1c ?? muted("—"))),
    },
    {
      id: "name",
      header: "Наименование",
      accessorFn: (r) => r.p.offer.name ?? r.p.offer.name_1c ?? "",
      cell: ({ row }) => {
        if (row.original.leaf) return null;
        const o = row.original.p.offer;
        const title = o.name ?? o.name_1c;
        return (
          <span className="truncate text-muted-foreground" title={[o.name, o.name_1c].filter(Boolean).join(" / ")}>
            {title ?? <span className="italic">нет названия</span>}
          </span>
        );
      },
    },
    {
      id: "demand",
      header: "Спрос FBO/день (28д)",
      meta: { num: true, title: "orders_fbo_28d витрины: заказов FBO в день, среднее за 28 дней; у товара — Σ по включённым регионам" },
      accessorFn: (r) => sortable(num(r, "orders_fbo_28d")),
      sortUndefined: "last",
      cell: ({ row }) => <PerDay v={num(row.original, "orders_fbo_28d")} />,
    },
    {
      id: "stock",
      header: "Остаток FBO",
      meta: { num: true, title: "fbo_stock витрины (оценка); у товара — Σ по включённым регионам" },
      accessorFn: (r) => sortable(num(r, "fbo_stock")),
      sortUndefined: "last",
      cell: ({ row }) => <Qty v={num(row.original, "fbo_stock")} />,
    },
    {
      id: "days",
      header: "Дни покрытия",
      meta: { num: true, title: "cover_days_total витрины: (FBO + FBS) / спрос в день; у товара — из сумм по включённым регионам" },
      accessorFn: (r) => sortable(num(r, "cover_days_total")),
      sortUndefined: "last",
      cell: ({ row }) => <Days v={num(row.original, "cover_days_total")} demand={num(row.original, "orders_28d")} />,
    },
    {
      id: "class",
      header: "Класс",
      enableSorting: false,
      cell: ({ row }) => {
        const l = row.original.leaf;
        if (!l) return null;
        const c = l.cell.deficit_class;
        if (c === null) return muted(NO_DEMAND);
        return (
          <span className={cn("rounded px-1", DEFICIT_STYLE[c].cls, c === "ok" && "border")} data-class={c}>
            {DEFICIT_STYLE[c].label}
          </span>
        );
      },
    },
    {
      id: "quant",
      header: "Квант",
      enableSorting: false,
      meta: { num: true, title: "Штук в коробе — спека канала WB в 1С (по nm, один на все размеры), как в «Распределении»" },
      cell: ({ row }) => <QuantCell q={row.original.p.quant} leaf={Boolean(row.original.leaf)} />,
    },
    {
      id: "qty",
      header: "Нужно",
      meta: { num: true, title: "У региона — ввод (или «до N дней»); у товара — Σ по включённым регионам. До округления до кванта" },
      accessorFn: (r) => r.sortQty,
      cell: ({ row }) => {
        const r = row.original;
        if (!r.leaf) return <b className="tabular-nums" data-testid="draft-product-qty">{int(r.agg!.qty)}</b>;
        if (r.leaf.key === null) return muted("—", "offer_key не определён (размер) — количества нет");
        return (
          <QtyInput
            leafKey={r.leaf.key}
            qty={ctx.current.draft.qty[r.leaf.key]}
            label={label(r)}
            disabled={!r.leaf.on}
            onQty={ctx.current.onQty}
            onCommit={ctx.current.onCommit}
          />
        );
      },
    },
    {
      id: "final",
      header: "С квантом",
      enableSorting: false,
      meta: {
        num: true,
        title:
          "«Нужно» вверх до кратного кванту по каждому региону; у товара — Σ по включённым регионам. " +
          "Серым — «Округлять до кванта» выключено (в Excel уйдёт «Нужно»)",
      },
      cell: ({ row }) => {
        const r = row.original;
        const { draft, round } = ctx.current;
        if (r.leaf && r.leaf.key === null) return muted("—", "offer_key не определён (размер) — количества нет");
        const need = r.leaf ? leafQty(r.leaf, draft.qty) : r.agg!.qty;
        const v = r.leaf ? leafFinal(r.p, r.leaf, draft.qty, round) : r.agg!.final;
        const problem = quantProblem(r.p.quant);
        return (
          <span
            className={cn("inline-flex items-center tabular-nums", !round && "text-muted-foreground", round && !r.leaf && "font-bold")}
            data-testid={r.leaf ? "draft-leaf-final" : "draft-product-final"}
            title={round ? undefined : "«Округлять до кванта» выключено — в Excel уйдёт «Нужно»"}
          >
            {int(v)}
            {round && problem !== null && need > 0 && warn(problem)}
          </span>
        );
      },
    },
  ];
}

interface ColMeta {
  num?: boolean;
  title?: string;
}

/** Виртуализированное тело — своё у вкладки (у «Распределения» — своё, локальное в его файле). */
function Body({ rows, sel, header }: { rows: Row<TRow>[]; sel: Draft["sel"]; header: React.ReactNode }): React.ReactElement {
  const parentRef = useRef<HTMLDivElement>(null);
  const virt = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_H,
    overscan: 8,
  });
  return (
    // шапка — внутри того же контейнера прокрутки (sticky): горизонтальный скролл у них общий
    <div ref={parentRef} className="max-h-[68vh] overflow-auto">
      {header}
      <div className={MIN_W} style={{ height: virt.getTotalSize(), position: "relative" }}>
        {virt.getVirtualItems().map((vi) => {
          const row = rows[vi.index];
          if (!row) return null;
          const r = row.original;
          const leaf = Boolean(r.leaf);
          return (
            <div
              key={row.id}
              data-testid={leaf ? "draft-leaf" : "draft-product"}
              data-off={leaf && !r.leaf!.on ? "1" : undefined}
              className={cn(
                "absolute left-0 top-0 grid w-full items-center border-b text-xs",
                !leaf && "cursor-pointer bg-accent/30 font-medium hover:bg-accent/60",
                !leaf && isMarked(sel, r.p) && "bg-primary/10",
                leaf && "hover:bg-accent/40",
                leaf && !r.leaf!.on && "opacity-50", // выключено — видно, но не планируется
              )}
              style={{ height: ROW_H, transform: `translateY(${vi.start}px)`, gridTemplateColumns: GRID }}
              onClick={row.getCanExpand() ? row.getToggleExpandedHandler() : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <div
                  key={cell.id}
                  className={cn(
                    "flex min-w-0 items-center overflow-hidden whitespace-nowrap px-2",
                    (cell.column.columnDef.meta as ColMeta | undefined)?.num && "justify-end tabular-nums",
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DraftTable({
  shown,
  draft,
  showOff,
  expanded,
  onExpandedChange,
  onQty,
  onToggleSel,
  round,
}: {
  shown: DraftProduct[];
  draft: Draft;
  round: boolean;
  showOff: boolean;
  expanded: ExpandedState;
  onExpandedChange: OnChangeFn<ExpandedState>;
  onQty: (key: string, qty: number | null) => void;
  onToggleSel: (key: string) => void;
}): React.ReactElement {
  const [sorting, setSorting] = useState<SortingState>([]);
  // снимок количеств для сортировки: обновляется по blur/Enter поля, а правки не из поля (заполнить,
  // очистить, другая вкладка) подхватываются сразу — пока фокус не в поле количества
  const [sortQty, setSortQty] = useState(draft.qty);
  const ctx = useRef<Ctx>({ draft, round, onQty, onToggleSel, onCommit: () => undefined });
  const onCommit = useCallback(() => setSortQty(ctx.current.draft.qty), []);
  ctx.current = { draft, round, onQty, onToggleSel, onCommit };
  useEffect(() => {
    const a = document.activeElement;
    if (a instanceof HTMLElement && a.hasAttribute("data-qty-input")) return;
    setSortQty(draft.qty);
  }, [draft.qty]);
  const tree = useMemo(
    () => buildTree(shown, draft.qty, sortQty, showOff, expanded, round),
    [shown, draft.qty, sortQty, showOff, expanded, round],
  );
  const cols = useMemo(() => columns(ctx), []);
  const table = useReactTable({
    data: tree,
    columns: cols,
    getRowId: (r) => r.id,
    getSubRows: (r) => (r.leaf ? undefined : r.subRows),
    // subRows у нераскрытых пусты (см. buildTree) — раскрываемость объявляем явно
    getRowCanExpand: (row) => row.original.nKids > 0,
    state: { sorting, expanded },
    onSortingChange: setSorting,
    onExpandedChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableSortingRemoval: true,
  });
  const header = (
    <div
      className={cn("sticky top-0 z-10 grid border-b bg-muted text-[11px] font-medium uppercase tracking-wide text-muted-foreground", MIN_W)}
      style={{ gridTemplateColumns: GRID }}
    >
      {table.getFlatHeaders().map((h) => {
        const meta = h.column.columnDef.meta as ColMeta | undefined;
        const dir = h.column.getIsSorted();
        const text = flexRender(h.column.columnDef.header, h.getContext());
        return (
          <div key={h.id} className={cn("flex items-center px-2 py-1.5", meta?.num && "justify-end text-right")} title={meta?.title}>
            {h.column.getCanSort() ? (
              <button type="button" className="flex items-center gap-0.5 uppercase hover:text-foreground" onClick={h.column.getToggleSortingHandler()}>
                {text}
                {dir === "asc" && <ArrowUp className="size-3" />}
                {dir === "desc" && <ArrowDown className="size-3" />}
              </button>
            ) : (
              text
            )}
          </div>
        );
      })}
    </div>
  );
  return (
    <div className="rounded-md border" data-testid="draft-table">
      <Body rows={table.getRowModel().rows} sel={draft.sel} header={header} />
    </div>
  );
}
